import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCommunity } from '@/contexts/CommunityContext';
import { useTranslationContext } from '@/contexts/TranslationContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Languages, Edit, Check, X, RotateCcw, Info, Sparkles, ThumbsUp, Users } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useBadges } from '@/hooks/useBadges';
import { getLocaleInfo } from '@/utils/localeUtils';

interface BadgeTranslation {
  badge_id: string;
  translation_key: string;
  default_value: string;
  current_translation?: string;
  has_override: boolean;
}

interface BadgeTranslationManagementProps {
  isOpen: boolean;
  onClose: () => void;
}

// Icon mapping for badge preview
const getIconComponent = (iconName: string) => {
  switch (iconName) {
    case 'Star':
      return Sparkles;
    case 'ThumbsUp':
      return ThumbsUp;
    case 'Users':
      return Users;
    default:
      return Sparkles;
  }
};

export const BadgeTranslationManagement: React.FC<BadgeTranslationManagementProps> = ({
  isOpen,
  onClose
}) => {
  const { currentCommunity } = useCommunity();
  const { availableLocales, locale: currentLocale } = useTranslationContext();
  const queryClient = useQueryClient();
  
  const [selectedLocale, setSelectedLocale] = useState<string>(currentLocale);
  const [editingTranslations, setEditingTranslations] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch badges with translation data
  const { data: badges, isLoading: badgesLoading } = useBadges(currentCommunity?.id || '');
  
  // Fetch community translations for selected locale
  const { data: communityTranslations, isLoading: translationsLoading } = useQuery({
    queryKey: ['badge-translations', currentCommunity?.id, selectedLocale],
    queryFn: async () => {
      if (!currentCommunity?.id) return [];
      
      const { data, error } = await supabase
        .from('community_translations')
        .select('*')
        .eq('community_id', currentCommunity.id)
        .eq('locale', selectedLocale)
        .like('translation_key', 'badge.%');
        
      if (error) throw error;
      return data;
    },
    enabled: !!currentCommunity?.id && !!selectedLocale
  });

  // Prepare badge translation data
  const badgeTranslations: BadgeTranslation[] = React.useMemo(() => {
    if (!badges) return [];
    
    return badges
      .filter(badge => badge.translation_key) // Only badges with translation keys
      .map(badge => {
        const communityTranslation = communityTranslations?.find(
          ct => ct.translation_key === badge.translation_key
        );
        
        return {
          badge_id: badge.id,
          translation_key: badge.translation_key!,
          default_value: badge.translatedName || badge.name,
          current_translation: communityTranslation?.value,
          has_override: !!communityTranslation
        };
      });
  }, [badges, communityTranslations]);

  // Save translations mutation
  const saveTranslationsMutation = useMutation({
    mutationFn: async (translations: Record<string, string>) => {
      if (!currentCommunity?.id) throw new Error('No community');
      
      const operations = [];
      
      for (const [translationKey, value] of Object.entries(translations)) {
        if (value.trim()) {
          // Upsert translation
          operations.push(
            supabase
              .from('community_translations')
              .upsert({
                community_id: currentCommunity.id,
                translation_key: translationKey,
                locale: selectedLocale,
                value: value.trim()
              })
          );
        } else {
          // Delete empty translation (revert to default)
          operations.push(
            supabase
              .from('community_translations')
              .delete()
              .eq('community_id', currentCommunity.id)
              .eq('translation_key', translationKey)
              .eq('locale', selectedLocale)
          );
        }
      }
      
      const results = await Promise.all(operations);
      results.forEach(result => {
        if (result.error) throw result.error;
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Badge translations saved successfully",
      });
      setEditingTranslations({});
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['badge-translations'] });
      queryClient.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save translations",
      });
    },
  });

  // Handle translation change
  const handleTranslationChange = (translationKey: string, value: string) => {
    setEditingTranslations(prev => ({
      ...prev,
      [translationKey]: value
    }));
    setHasUnsavedChanges(true);
  };

  // Reset to default
  const resetTranslation = (translationKey: string) => {
    setEditingTranslations(prev => ({
      ...prev,
      [translationKey]: ''
    }));
    setHasUnsavedChanges(true);
  };

  // Save all changes
  const handleSave = () => {
    saveTranslationsMutation.mutate(editingTranslations);
  };

  // Get current translation value for display
  const getCurrentTranslation = (badgeTranslation: BadgeTranslation): string => {
    if (editingTranslations.hasOwnProperty(badgeTranslation.translation_key)) {
      return editingTranslations[badgeTranslation.translation_key];
    }
    return badgeTranslation.current_translation || badgeTranslation.default_value;
  };

  const isLoading = badgesLoading || translationsLoading;
  const hasChanges = Object.keys(editingTranslations).length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5" />
            Badge Translation Management
          </DialogTitle>
          <DialogDescription>
            Customize how badge names appear in your community. Override default translations
            or keep the original names.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {/* Locale Selection */}
          <div className="mb-4">
            <Label className="text-sm font-medium">Language</Label>
            <Tabs value={selectedLocale} onValueChange={setSelectedLocale} className="mt-1">
              <TabsList>
                {availableLocales.map((locale) => {
                  const localeInfo = getLocaleInfo(locale);
                  return (
                    <TabsTrigger key={locale} value={locale} className="text-xs">
                      {localeInfo.flag} {localeInfo.name}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
          </div>

          {/* Status Alert */}
          {hasUnsavedChanges && (
            <Alert className="mb-4">
              <Info className="h-4 w-4" />
              <AlertDescription>
                You have unsaved changes. Click "Save Changes" to apply them.
              </AlertDescription>
            </Alert>
          )}

          {/* Translation Table */}
          <div className="border rounded-lg overflow-hidden flex-1">
            {isLoading ? (
              <div className="p-6 space-y-4">
                {Array(3).fill(0).map((_, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-[200px]" />
                      <Skeleton className="h-4 w-[150px]" />
                    </div>
                    <Skeleton className="h-8 w-[200px]" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="overflow-auto max-h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Badge</TableHead>
                      <TableHead className="w-[200px]">Default Name</TableHead>
                      <TableHead>Custom Translation</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {badgeTranslations.map((badgeTranslation) => {
                      const badge = badges?.find(b => b.id === badgeTranslation.badge_id);
                      const IconComponent = getIconComponent(badge?.icon || 'Star');
                      const currentTranslation = getCurrentTranslation(badgeTranslation);
                      const isModified = editingTranslations.hasOwnProperty(badgeTranslation.translation_key);
                      const hasOverride = badgeTranslation.has_override || isModified;

                      return (
                        <TableRow key={badgeTranslation.badge_id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div 
                                className="flex items-center justify-center w-8 h-8 rounded-full"
                                style={{ backgroundColor: (badge?.color || '#f97316') + '20' }}
                              >
                                <IconComponent 
                                  className="w-4 h-4"
                                  style={{ color: badge?.color || '#f97316' }}
                                />
                              </div>
                              <div>
                                <div className="font-medium text-sm">
                                  {badge?.name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {badgeTranslation.translation_key}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-muted-foreground">
                              {badgeTranslation.default_value}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Input
                                value={currentTranslation}
                                onChange={(e) => handleTranslationChange(
                                  badgeTranslation.translation_key, 
                                  e.target.value
                                )}
                                placeholder={badgeTranslation.default_value}
                                className="flex-1"
                              />
                              {hasOverride && (
                                <Badge variant="secondary" className="text-xs">
                                  Custom
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => resetTranslation(badgeTranslation.translation_key)}
                                    disabled={!hasOverride}
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Reset to default</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex items-center text-sm text-muted-foreground">
            {badgeTranslations.length} badge{badgeTranslations.length !== 1 ? 's' : ''} available
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button 
              onClick={handleSave}
              disabled={!hasChanges || saveTranslationsMutation.isPending}
            >
              {saveTranslationsMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};